import { computed, isRef, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import getGames from 'src/apollo/queries/getGames.gql'
import getTournamentsGames from 'src/apollo/queries/getTournamentsGames.gql'
import getFavoritesGames from 'src/apollo/queries/getFavoritesGames.gql'
import getUserLatestGames from 'src/apollo/queries/getUserLatestGames.gql'
import getFastCashbackGames from 'src/apollo/queries/getFastCashbackGames.gql'
import getUserPromoGames from 'src/apollo/queries/getUserPromoGames.gql'

export function useGamesList(payload, options) {
  const data = computed(() => {
    return isRef(payload) ? payload.value : payload
  })
  const isFavorite = computed(() => data.value.group === 'favorites')
  const isTournamentGroup = computed(() => !isFavorite.value && data.value.subgroup === 'tournament')
  const isLatestUserGames = computed(() => data.value.group === 'latest')
  const isFastCashbackGames = computed(() => data.value.subgroup === 'cashback')
  const isPromoGames = computed(() => data.value.group === 'promo-games')

  const dataForApollo = computed(() => {
    const variables = {}
    let query = getGames

    // Common data for any type
    if (data.value.manufacturer) {
      variables.manufacturer = data.value.manufacturer
    }

    if (isLatestUserGames.value) {
      return {
        query: getUserLatestGames,
      }
    }

    if (isFavorite.value) {
      query = getFavoritesGames
      return {
        variables,
        query,
      }
    }
    if (isTournamentGroup.value) {
      query = getTournamentsGames
    }

    if (isFastCashbackGames.value) {
      query = getFastCashbackGames
      variables.groupSlug = data.value.group
    }

    if (isPromoGames.value) {
      query = getUserPromoGames
    }

    if (data.value.group) {
      variables.group = data.value.group
    }
    if (data.value.subgroup) {
      variables.subgroup = data.value.subgroup
    }
    if (data.value.search) {
      variables.search = data.value.search
    }

    if (data.value.subgroups) {
      variables.subgroups = data.value.subgroups
    }

    return {
      variables,
      query,
    }
  })

  const usedLoadMore = ref(false)
  const loadingInit = computed(() => !usedLoadMore.value && loading.value)
  const { result, fetchMore, loading } = useQuery(
    dataForApollo.value.query,
    () => dataForApollo.value.variables,
    () => ({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      ...(isRef(options) ? options.value : options),
    })
  )

  const gamesListData = computed(() => {
    const data = result.value
    if (!data) {
      return
    }

    if (isLatestUserGames.value) {
      const games = data.userLatestedGames.map((i) => i.game)
      return {
        games,
        hasNextPage: false,
        totalCount: games.length,
      }
    }

    if (isFavorite.value) {
      const games = data.currentUser.favorites.edges.map((i) => i.node)
      const hasNextPage = data.currentUser.favorites.hasNextPage
      const totalCount = data.currentUser.favorites.totalCount
      return {
        games,
        hasNextPage,
        totalCount,
      }
    }
    if (isTournamentGroup.value) {
      const games = data.competitionsGames.edges.map((i) => i.node)
      const hasNextPage = data.competitionsGames.hasNextPage
      const totalCount = data.competitionsGames.totalCount
      return {
        games,
        hasNextPage,
        totalCount,
      }
    }
    if (isFastCashbackGames.value) {
      const games = data.fastCashbackGames.edges.map((i) => i.node)
      const hasNextPage = data.fastCashbackGames.hasNextPage
      const totalCount = data.fastCashbackGames.totalCount
      return {
        games,
        hasNextPage,
        totalCount,
      }
    }
    if (isPromoGames.value) {
      const games = data.userPromo.activeStep.gamesList?.edges?.map((i) => i.node)
      const hasNextPage = data.userPromo.activeStep.gamesList?.hasNextPage
      const totalCount = data.userPromo.activeStep.gamesList?.totalCount
      return {
        games,
        hasNextPage,
        totalCount,
      }
    }

    const games = data.sortedGames.edges.map((i) => i.node)
    const hasNextPage = data.sortedGames.hasNextPage
    const totalCount = data.sortedGames.totalCount
    return {
      games,
      hasNextPage,
      totalCount,
    }
  })
  const games = computed(() => {
    if (isFavorite.value) {
      return (gamesListData.value?.games ?? []).filter((game) => game.isFavorite)
    }

    return gamesListData.value?.games ?? []
  })
  const hasNextPage = computed(() => {
    return gamesListData.value?.hasNextPage ?? false
  })
  const totalCount = computed(() => {
    return gamesListData.value?.totalCount ?? 0
  })

  const alreadyLoaded = new Set()

  function loadMore() {
    const variables = {
      ...dataForApollo.value.variables,
      offset: games.value.length,
    }

    const uniqStringWithVariables = JSON.stringify(variables)
    if (alreadyLoaded.has(uniqStringWithVariables)) {
      return
    }

    alreadyLoaded.add(uniqStringWithVariables)

    usedLoadMore.value = true
    return fetchMore({
      variables,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        // For favorite games
        if (isFavorite.value) {
          const newGames = fetchMoreResult.currentUser.favorites.edges
          const hasNextPage = fetchMoreResult.currentUser.favorites.hasNextPage

          return {
            currentUser: {
              ...previousResult.currentUser,
              favorites: {
                ...previousResult.currentUser.favorites,
                edges: [...previousResult.currentUser.favorites.edges, ...newGames],
                hasNextPage,
              },
            },
          }
        }
        // For tournament games
        if (isTournamentGroup.value) {
          const newGames = fetchMoreResult.competitionsGames.edges
          const hasNextPage = fetchMoreResult.competitionsGames.hasNextPage

          return {
            competitionsGames: {
              ...previousResult.competitionsGames,
              edges: [...previousResult.competitionsGames.edges, ...newGames],
              hasNextPage,
            },
          }
        }
        // For fast cashback games
        if (isFastCashbackGames.value) {
          const newGames = fetchMoreResult.fastCashbackGames.edges
          const hasNextPage = fetchMoreResult.fastCashbackGames.hasNextPage

          return {
            fastCashbackGames: {
              ...previousResult.fastCashbackGames,
              edges: [...previousResult.fastCashbackGames.edges, ...newGames],
              hasNextPage,
            },
          }
        }
        // For promo-games
        if (isPromoGames.value) {
          const newGames = fetchMoreResult.userPromo.activeStep.gamesList.edges
          const hasNextPage = fetchMoreResult.userPromo.activeStep.gamesList.hasNextPage

          return {
            userPromo: {
              activeStep: {
                ...previousResult.userPromo.activeStep,
                gamesList: {
                  ...previousResult.userPromo.activeStep.gamesList,
                  edges: [...previousResult.userPromo.activeStep.gamesList.edges, ...newGames],
                  hasNextPage,
                },
              },
            },
          }
        }

        const newGames = fetchMoreResult.sortedGames.edges
        const hasNextPage = fetchMoreResult.sortedGames.hasNextPage
        const totalCount = fetchMoreResult.sortedGames.totalCount
        return {
          sortedGames: {
            ...previousResult.sortedGames,
            edges: [...previousResult.sortedGames.edges, ...newGames],
            hasNextPage,
            totalCount,
          },
        }
      },
    })
  }

  return { games, hasNextPage, loading, loadingInit, totalCount, loadMore }
}
